import React, { ReactElement } from 'react';
import moment from 'moment';
import { LocationTaskDetails } from './Types';
import { Option } from '../../../../types/common';
import TaskMessage from './TaskMessage';

// Icons - using Font Awesome classes since the component already uses them
const InfoIcon = (): ReactElement => <i className="pi pi-info-circle" style={{ color: '#3b82f6', fontSize: '14px' }} />;
const CheckCircleIcon = (): ReactElement => <i className="pi pi-check-circle" style={{ color: '#059669', fontSize: '14px' }} />;
const ClockIcon = (): ReactElement => <i className="pi pi-clock" style={{ color: '#d97706', fontSize: '14px' }} />;
const AlertCircleIcon = (): ReactElement => <i className="pi pi-exclamation-circle" style={{ color: '#dc2626', fontSize: '14px' }} />;
const UploadIcon = (): ReactElement => <i className="pi pi-upload" style={{ color: '#3b82f6', fontSize: '14px' }} />;
const FileCheckIcon = (): ReactElement => <i className="pi pi-file-check" style={{ color: '#3b82f6', fontSize: '14px' }} />;
const HelpCircleIcon = (): ReactElement => <i className="pi pi-question-circle" style={{ color: '#3b82f6', fontSize: '14px' }} />;

interface TaskMessagesProps {
    locationTaskDetails: LocationTaskDetails | null;
    statuses: Option[];
    onTaskClick?: (taskId: number) => void;
    isProjectCertified: boolean;
    onCertificationChange?: (certified: boolean) => void;
}

interface TaskMessage {
    type: string;
    icon: ReactElement;
    content: ReactElement;
}

const TaskMessages = ({
    locationTaskDetails,
    statuses,
    onTaskClick,
    isProjectCertified,
    onCertificationChange
}: TaskMessagesProps): ReactElement | null => {
    if (!locationTaskDetails?.locationTask) {
        return null;
    }

    const { locationTask } = locationTaskDetails;
    const messages: TaskMessage[] = [];

    // Helper function to get status colors and icons based on Core Status ID
    const getStatusDisplay = (coreStatusId?: number) => {
        switch (coreStatusId) {
            case 1: // Not Started
                return {
                    icon: <ClockIcon />,
                    text: 'Not Started:',
                    className: 'status-not-started'
                };

            case 3: // In Progress / Active / Partially Complete
                return {
                    icon: <ClockIcon />,
                    text: 'In Progress:',
                    className: 'status-in-progress'
                };

            case 4: // Complete / Done
                return {
                    icon: <CheckCircleIcon />,
                    text: 'Completed:',
                    className: 'status-completed'
                };

            default: // Past Due or Unknown
                return {
                    icon: <AlertCircleIcon />,
                    text: 'Past Due:',
                    className: 'status-past-due'
                };
        }
    };

    if (locationTaskDetails.contingentTaskName) {
        const taskStatus = locationTaskDetails.contingentTaskStatus;

        // Try to find the status in the statuses array to get core status ID
        const matchedStatus = statuses.find(s => s.name.toLowerCase() === taskStatus?.toLowerCase());
        const coreStatusId = (matchedStatus as any)?.coreStatusId;

        // Use past due logic as fallback for non-matched statuses
        const isPastDue = taskStatus === "Past due";
        const statusDisplay = isPastDue ?
            getStatusDisplay(undefined) : // Past due (default case)
            getStatusDisplay(coreStatusId);

        messages.push({
            type: 'prerequisite-of',
            icon: <InfoIcon />,
            content: (
                <>
                    <span className="font-bold" style={{ color: '#3B82F6' }}>This task is a prerequisite of: </span>
                    <div key={locationTask.contingentId} className="prerequisite-item">
                        {statusDisplay.icon}
                        <span style={{ fontSize: '11px' }}>
                            <span className={statusDisplay.className}>{statusDisplay.text}</span>{' '}
                            <button
                                className="task-link"
                                onClick={() => onTaskClick?.(locationTask.contingentId ?? 0)}
                            >
                                {locationTaskDetails.contingentTaskName.length > 50 ? `${locationTaskDetails.contingentTaskName.substring(0, 47)}...` : locationTaskDetails.contingentTaskName}
                            </button>
                        </span>
                    </div>
                </>
            )
        });
    }

    if (locationTaskDetails.successorTasks && locationTaskDetails.successorTasks.length > 0) {
        messages.push({
            type: 'has-prerequisites',
            icon: <InfoIcon />,
            content: (
                <div>
                    <span className="font-bold" style={{ color: '#3B82F6' }}>This task has prerequisites that must be completed first:</span>
                    <div>
                        {locationTaskDetails.successorTasks.map((task) => {
                            const taskStatus = statuses.find(s => s.id === task.statusId);
                            const coreStatusId = (taskStatus as any)?.coreStatusId;

                            // Check if task is past due based on date
                            const isPastDue = task.targetDate && moment(task.targetDate).isBefore(moment()) && coreStatusId !== 4; // Not past due if already complete
                            const daysUntilDue = task.targetDate ? moment(task.targetDate).diff(moment(), 'days') : null;

                            // Use core status ID for status display, but override with past due if applicable
                            const statusDisplay = isPastDue ?
                                getStatusDisplay(undefined) : // Past due (default case)
                                getStatusDisplay(coreStatusId);

                            let timeInfo: ReactElement | null = null;
                            if (isPastDue) {
                                timeInfo = <span className="overdue-info">({Math.abs(daysUntilDue || 0)} days overdue)</span>;
                            } else if (coreStatusId !== 4) { // Don't show "due in X days" for completed tasks
                                if (daysUntilDue !== null && daysUntilDue > 0) {
                                    timeInfo = <span className="due-info">(Due in {daysUntilDue} days)</span>;
                                } else if (daysUntilDue === 0) {
                                    timeInfo = <span className="due-info">(Due today)</span>;
                                }
                            }

                            return (
                                <div key={task.id} className="prerequisite-item">
                                    {statusDisplay.icon}
                                    <span style={{ fontSize: '11px' }}>
                                        <span className={statusDisplay.className}>{statusDisplay.text}</span>{' '}
                                        <button
                                            className="task-link"
                                            onClick={() => onTaskClick?.(task.id)}
                                        >
                                            {task.name.length > 50 ? `${task.name.substring(0, 47)}...` : task.name}
                                        </button>
                                        {timeInfo}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )
        });
    }

    const requiresCertification = locationTask.includeCertificationCheckbox;
    // prereq also on second line also with status
    if (requiresCertification) {//fix indentation
        messages.push({
            type: 'certification-required',
            icon: <FileCheckIcon />,
            content: (
                <div>
                    <div>
                        <span className="font-bold" style={{ color: '#3B82F6' }}>Certification Required: </span>
                        <span style={{ color: '#3B82F6' }}>You must certify completion before marking this task as complete.</span>
                    </div>
                    <div className="certification-checkbox">
                        <input
                            type="checkbox"
                            id={`cert-${locationTask.id}`}
                            checked={isProjectCertified}
                            onChange={(e) => onCertificationChange?.(e.target.checked)}
                        />
                        <label htmlFor={`cert-${locationTask.id}`} style={{ position: 'relative', top: '4px' }}>
                            I certify that I have completed all requirements for this task and understand
                            the responsibilities outlined.
                        </label>
                    </div>
                </div>
            )
        });
    }

    if (messages.length === 0) {
        return null;
    }

    return (
        <div className="task-messages-section">
            <div className="messages-content">
                {messages.map((message, index) => (
                    <TaskMessage
                        key={`${message.type}-${index}`}
                        icon={message.icon}
                        content={message.content}
                    />
                ))}
            </div>
        </div>
    );
};

export default TaskMessages;